import { useCallback, useEffect } from 'react';

import { logger } from '../datadog';
import { noop } from '../utils';
import {
  BackgroundType,
  getStoredAudioVideoSettings,
  localVideoContextAtom,
  roomAtom,
  useSwitchBackground,
} from '../video-sdk';
import { useAtomCallbackMemoized } from './useAtomCallbackMemoized';
import { useOnInactiveScreenDetected } from './useOnInactiveScreenDetected';
import { useVideoPlatform } from './useVideoPlatform';

export function useInactiveTabPerformanceOptimization() {
  const changeBackground = useSwitchBackground();
  const getLocalVideoDelegator = useAtomCallbackMemoized((get) => get(localVideoContextAtom).delegator, []);
  const getIsInSession = useAtomCallbackMemoized((get) => !!get(roomAtom), []);
  const videoPlatform = useVideoPlatform();
  const isZoomEnabled = videoPlatform === 'zoom';

  const handleLeaveCallback = useCallback(async () => {
    const localVideoDelegator = getLocalVideoDelegator();
    const { backgroundType = BackgroundType.NONE, backgroundIndex } = getStoredAudioVideoSettings();
    const isInSession = getIsInSession();

    // if there's no video track or the video track is disabled,
    // we don't need to do anything
    if (!localVideoDelegator?.isEnabled) return;
    // if there's no background, we don't need to do anything
    if (backgroundType === BackgroundType.NONE) return;
    // if someone could be viewing the video, don't disable the background
    if (isInSession) return;

    logger.debug('Disabling video background processor while tab is inactive', {
      backgroundType,
      backgroundIndex,
    });

    await changeBackground({ backgroundType: BackgroundType.NONE }, { persistChanges: false });
  }, [getIsInSession, getLocalVideoDelegator, changeBackground]);

  const handleReturnCallback = useCallback(async () => {
    const localVideoTrack = getLocalVideoDelegator();

    // if there's no video track or it is disabled, and since we didn't modify
    // saved settings, nothing to do, allowing the onEnable action to restore
    // the background
    if (!localVideoTrack?.isEnabled) return;

    const { backgroundType = BackgroundType.NONE, backgroundIndex } = getStoredAudioVideoSettings();

    if (backgroundType === BackgroundType.NONE) return;

    logger.debug('Restoring video background processor from previously active tab', {
      backgroundType,
      backgroundIndex,
    });

    await changeBackground({ backgroundType, backgroundIndex }, { persistChanges: false });
  }, [getLocalVideoDelegator, changeBackground]);

  const handleLeave = isZoomEnabled ? noop : handleLeaveCallback;

  // the return callback is called in session which the Zoom adapter does not
  // expect, also Zooms local video preview already takes care of pausing video
  // when the tab is inactive so this hook is unnecessary for Zoom
  const handleReturn = isZoomEnabled ? noop : handleReturnCallback;

  useOnInactiveScreenDetected(handleLeave, handleReturn);

  // cleanup on unmount
  // we use a reference to a video component *shared* across pages, so if,
  // for example, a Provider admitted a Client, and we display a new page,
  // the background would not be restored when in the session
  useEffect(
    function cleanupOnUnmount() {
      return function cleanup() {
        // useEffect doesn't like async functions
        handleReturn();
      };
    },
    [handleReturn],
  );
}
